import { useParams, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, ArrowUpRight, Check } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { ContactForm } from "../components/ContactForm";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { FaqSection } from "../sections/FaqSection";
import { useUI } from "../context/UIContext";
import { getCity, CITIES, SERVICE_LIST } from "../data/locations";
import { getState, stateSlugByName } from "../data/states";
import { BRANCHEN_LIST } from "../data/branchen";

const breadcrumbSchema = (items) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: items.map((it, i) => ({
    "@type": "ListItem", position: i + 1, name: it.name,
    item: `https://www.energie-tarifberater.de${it.path}`,
  })),
});

export default function StandortHub() {
  const { slug } = useParams();
  const { openUpload } = useUI();
  const city = getCity(slug);
  const state = getState(slug);

  if (!city && !state) return <Navigate to="/standorte" replace />;

  if (!city && state) {
    const cities = CITIES.filter((c) => c.state === state.name);
    const path = `/standorte/${state.slug}`;
    return (
      <>
        <Seo title={`Energieberatung ${state.name}`}
          description={`Unabhängige Strom-, Gas- & Gewerbestromberatung in ${state.name}. Regionale Beratung, kostenlose Rechnungsprüfung und Wechselservice.`}
          path={path}
          schema={breadcrumbSchema([{ name: "Start", path: "/" }, { name: "Standorte", path: "/standorte" }, { name: state.name, path }])} />
        <PageHero eyebrow="Bundesland" titleLines={["Energieberatung", `in <span class="font-serif italic text-blue-700">${state.name}</span>`]}
          subtitle={state.intro} breadcrumb={[{ label: "Standorte", to: "/standorte" }, { label: state.name }]} />
        <section className="bg-background py-24 sm:py-32">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <Reveal>
                <h2 className="text-2xl font-heading text-navy-900 mb-6">Regionale Besonderheiten</h2>
                <div className="space-y-4 mb-12">
                  {state.bes.map((b, i) => (
                    <div key={i} className="flex gap-4 rounded-2xl border border-slate-200 bg-white p-6" data-testid={`state-bes-${i}`}>
                      <span className="shrink-0 inline-grid place-items-center h-10 w-10 rounded-xl bg-blue-50 text-blue-700"><Check className="h-5 w-5" strokeWidth={2.5} /></span>
                      <p className="text-navy-900 text-sm leading-relaxed pt-1.5">{b}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
              <Reveal>
                <h2 className="text-2xl font-heading text-navy-900 mb-4">Leistungen in {state.name}</h2>
                <div className="flex flex-wrap gap-3 mb-12">
                  {SERVICE_LIST.map((s) => (
                    <Link key={s.slug} to={`/${s.slug}`} className="rounded-full border border-slate-300 hover:border-blue-500 hover:text-blue-700 px-4 py-2 text-sm text-slate-700 transition-colors" data-testid={`state-service-${s.slug}`}>{s.label}</Link>
                  ))}
                </div>
              </Reveal>
              <Reveal>
                <h2 className="text-2xl font-heading text-navy-900 mb-4">Städte in {state.name}</h2>
                <div className="grid sm:grid-cols-2 gap-3">
                  {cities.map((c) => (
                    <Link key={c.slug} to={`/standorte/${c.slug}`} className="group flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-5 py-4 hover:border-blue-400 transition-colors" data-testid={`state-city-${c.slug}`}>
                      <span className="flex items-center gap-2"><MapPin className="h-4 w-4 text-blue-600" /><span className="font-heading text-navy-900">{c.name}</span></span>
                      <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-blue-700" />
                    </Link>
                  ))}
                </div>
              </Reveal>
            </div>
            <div className="lg:col-span-5">
              <div className="lg:sticky lg:top-28 rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.15)]">
                <p className="text-xs uppercase tracking-[0.2em] text-blue-700 mb-2">Kostenlose Analyse · {state.name}</p>
                <h3 className="text-2xl font-heading font-semibold text-navy-900 mb-6">Angebot in <span className="font-serif italic text-blue-700">24 Stunden</span></h3>
                <button onClick={openUpload} className="w-full group rounded-full bg-blue-700 hover:bg-blue-600 text-white py-4 font-medium transition-colors flex items-center justify-center gap-2 mb-5" data-testid="state-upload-btn">Rechnung hochladen <ArrowUpRight className="h-4 w-4" /></button>
                <ContactForm compact topic={`bundesland-${state.slug}`} testid={`state-form-${state.slug}`} />
              </div>
            </div>
          </div>
        </section>
        <FaqSection />
      </>
    );
  }

  // ---- City hub ----
  const stateSlug = stateSlugByName(city.state);
  const path = `/standorte/${city.slug}`;
  return (
    <>
      <Seo title={`Energieberatung ${city.name}`}
        description={`Unabhängige Strom-, Gas- & Gewerbestromberatung in ${city.name}. Kostenlose Rechnungsprüfung, Rückruf und Wechselservice – persönlich in ${city.state}.`}
        path={path}
        schema={breadcrumbSchema([{ name: "Start", path: "/" }, { name: "Standorte", path: "/standorte" }, { name: city.name, path }])} />
      <PageHero eyebrow={`Standort · ${city.state}`} titleLines={["Energieberatung", `in <span class="font-serif italic text-blue-700">${city.name}</span>`]}
        subtitle={`Ihre unabhängige Anlaufstelle für Strom, Gas und Gewerbestrom in ${city.name}. Kostenlose Prüfung, persönliche Beratung, kompletter Wechselservice.`}
        breadcrumb={[{ label: "Standorte", to: "/standorte" }, { label: city.name }]} />
      <section className="bg-background py-24 sm:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <Reveal><h2 className="text-2xl font-heading text-navy-900 mb-6">Leistungen in {city.name}</h2></Reveal>
            <div className="grid sm:grid-cols-2 gap-4 mb-12">
              {SERVICE_LIST.map((s, i) => (
                <Reveal key={s.slug} delay={i * 0.05}>
                  <Link to={`/${s.slug}/${city.slug}`} className="group flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-6 py-5 hover:border-blue-400 hover:-translate-y-0.5 transition-[transform,border-color]" data-testid={`cityhub-service-${s.slug}`}>
                    <span className="font-heading text-navy-900">{s.label} {city.name}</span>
                    <ArrowUpRight className="h-4 w-4 text-slate-400 group-hover:text-blue-700" />
                  </Link>
                </Reveal>
              ))}
            </div>
            <Reveal>
              <div className="flex flex-wrap gap-3 mb-4">
                <Link to={`/standorte/${stateSlug}`} className="rounded-full bg-blue-50 text-blue-700 px-4 py-2 text-sm font-medium hover:bg-blue-100 transition-colors" data-testid="cityhub-state-link">Alle Städte in {city.state}</Link>
                <Link to="/branchen" className="rounded-full border border-slate-300 px-4 py-2 text-sm text-slate-700 hover:border-blue-500 hover:text-blue-700 transition-colors" data-testid="cityhub-branchen-link">Branchenlösungen</Link>
              </div>
              <div className="flex flex-wrap gap-x-5 gap-y-2 mt-4">
                {BRANCHEN_LIST.slice(0, 6).map((b) => (
                  <Link key={b.slug} to={`/branche/${b.slug}`} className="text-sm text-slate-500 hover:text-blue-700 transition-colors" data-testid={`cityhub-branche-${b.slug}`}>{b.name}</Link>
                ))}
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 rounded-3xl border border-slate-200 bg-white p-8 shadow-[0_40px_80px_-40px_rgba(0,0,0,0.15)]">
              <p className="text-xs uppercase tracking-[0.2em] text-blue-700 mb-2">Rückruf & Analyse · {city.name}</p>
              <h3 className="text-2xl font-heading font-semibold text-navy-900 mb-6">Persönlich für <span className="font-serif italic text-blue-700">Sie da</span></h3>
              <button onClick={openUpload} className="w-full group rounded-full bg-blue-700 hover:bg-blue-600 text-white py-4 font-medium transition-colors flex items-center justify-center gap-2 mb-5" data-testid="cityhub-upload-btn">Rechnung hochladen <ArrowUpRight className="h-4 w-4" /></button>
              <ContactForm topic={`standort-${city.slug}`} testid={`cityhub-form-${city.slug}`} />
            </div>
          </div>
        </div>
      </section>
      <FaqSection />
    </>
  );
}
